import { useState, useEffect, useRef } from 'react';

export interface CharacterPosition {
  faceX: number;
  faceY: number;
  bodySkew: number;
}

interface Props {
  isTyping: boolean;
  password: string;
  showPassword: boolean;
}

const DEFAULT_POS: CharacterPosition = { faceX: 0, faceY: 0, bodySkew: 0 };

const BLINK_INTERVAL_MIN_MS = 3000;
const BLINK_INTERVAL_RANGE_MS = 4000;
const BLINK_DURATION_MS = 150;
const PEEK_DELAY_MIN_MS = 2000;
const PEEK_DELAY_RANGE_MS = 3000;
const PEEK_DURATION_MS = 800;
const LOOK_AT_EACH_OTHER_DURATION_MS = 800;

export function useCharacterController({ isTyping, password, showPassword }: Props) {
  const targetRef = useRef({ x: 0, y: 0 });
  const smoothRef = useRef({ x: 0, y: 0 });
  const rafRef = useRef<number>(0);

  const [isPurpleBlinking, setIsPurpleBlinking] = useState(false);
  const [isBlackBlinking, setIsBlackBlinking] = useState(false);
  const [isLookingAtEachOther, setIsLookingAtEachOther] = useState(false);
  const [isPurplePeeking, setIsPurplePeeking] = useState(false);
  const [positions, setPositions] = useState({
    purple: DEFAULT_POS,
    black: DEFAULT_POS,
    yellow: DEFAULT_POS,
    orange: DEFAULT_POS,
  });

  const purpleRef = useRef<HTMLDivElement>(null);
  const blackRef = useRef<HTMLDivElement>(null);
  const yellowRef = useRef<HTMLDivElement>(null);
  const orangeRef = useRef<HTMLDivElement>(null);
  // Хранит актуальную функцию анимации: второй useEffect перезапускает цикл
  // через animateRef, не добавляя animate в свои зависимости.
  const animateRef = useRef<() => void>(() => {});

  useEffect(() => {
    const calcPos = (el: HTMLDivElement | null, x: number, y: number): CharacterPosition => {
      if (!el) return DEFAULT_POS;
      const rect = el.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 3;
      const deltaX = x - centerX;
      const deltaY = y - centerY;
      return {
        faceX: Math.max(-15, Math.min(15, deltaX / 20)),
        faceY: Math.max(-10, Math.min(10, deltaY / 30)),
        bodySkew: Math.max(-6, Math.min(6, -deltaX / 120)),
      };
    };

    const animate = () => {
      const s = smoothRef.current;
      const t = targetRef.current;
      s.x += (t.x - s.x) * 0.12;
      s.y += (t.y - s.y) * 0.12;
      setPositions({
        purple: calcPos(purpleRef.current, s.x, s.y),
        black: calcPos(blackRef.current, s.x, s.y),
        yellow: calcPos(yellowRef.current, s.x, s.y),
        orange: calcPos(orangeRef.current, s.x, s.y),
      });
      if (Math.abs(t.x - s.x) > 0.1 || Math.abs(t.y - s.y) > 0.1) {
        rafRef.current = requestAnimationFrame(animate);
      } else {
        rafRef.current = 0;
      }
    };

    animateRef.current = animate;

    const onMove = (e: MouseEvent) => {
      targetRef.current = { x: e.clientX, y: e.clientY };
      if (!rafRef.current) {
        rafRef.current = requestAnimationFrame(animate);
      }
    };

    window.addEventListener('mousemove', onMove);
    rafRef.current = requestAnimationFrame(animate);
    return () => {
      window.removeEventListener('mousemove', onMove);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  useEffect(() => {
    if (!rafRef.current) rafRef.current = requestAnimationFrame(animateRef.current);
  }, [isTyping, password, showPassword]);

  useEffect(() => {
    const schedule = () =>
      setTimeout(
        () => {
          setIsPurpleBlinking(true);
          // eslint-disable-next-line @eslint-react/web-api-no-leaked-timeout
          setTimeout(() => {
            setIsPurpleBlinking(false);
            schedule();
          }, BLINK_DURATION_MS);
        },
        Math.random() * BLINK_INTERVAL_RANGE_MS + BLINK_INTERVAL_MIN_MS
      );
    const t = schedule();
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const schedule = () =>
      setTimeout(
        () => {
          setIsBlackBlinking(true);
          // eslint-disable-next-line @eslint-react/web-api-no-leaked-timeout
          setTimeout(() => {
            setIsBlackBlinking(false);
            schedule();
          }, BLINK_DURATION_MS);
        },
        Math.random() * BLINK_INTERVAL_RANGE_MS + BLINK_INTERVAL_MIN_MS
      );
    const t = schedule();
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (isTyping) {
      const t0 = setTimeout(() => setIsLookingAtEachOther(true), 0);
      const t1 = setTimeout(() => setIsLookingAtEachOther(false), LOOK_AT_EACH_OTHER_DURATION_MS);
      return () => {
        clearTimeout(t0);
        clearTimeout(t1);
      };
    }
    const t = setTimeout(() => setIsLookingAtEachOther(false), 0);
    return () => clearTimeout(t);
  }, [isTyping]);

  useEffect(() => {
    if (password.length > 0 && showPassword) {
      const t = setTimeout(
        () => {
          setIsPurplePeeking(true);
          // eslint-disable-next-line @eslint-react/web-api-no-leaked-timeout
          setTimeout(() => setIsPurplePeeking(false), PEEK_DURATION_MS);
        },
        Math.random() * PEEK_DELAY_RANGE_MS + PEEK_DELAY_MIN_MS
      );
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setIsPurplePeeking(false), 0);
    return () => clearTimeout(t);
    // isPurplePeeking в зависимостях: когда peek заканчивается (false),
    // эффект перезапускается и может запустить следующий цикл.
  }, [password, showPassword, isPurplePeeking]);

  return {
    refs: { purpleRef, blackRef, yellowRef, orangeRef },
    positions,
    isPurpleBlinking,
    isBlackBlinking,
    isLookingAtEachOther,
    isPurplePeeking,
  };
}
